import { useCallback, useState } from 'react';
import { useVideosContext } from '../../../../contexts/VideosContext';
import { useVideoFilterOptions } from '../../../../hooks/useVideoFilterOptions';
import {
  hasActiveVideoFilters,
  type VideoTag,
  type VideoWithProspect,
} from '../../../../types/prospect-media';
import { ErrorBoundary } from '../../../../components/ErrorBoundary';
import { VideoFilterBar } from '../../../../components/VideoFilterBar';
import { VideosGrid } from './VideosGrid';
import { VideoViewerPanel } from './VideoViewerPanel';
import { VideoClipEditor } from '../players/videos/VideoClipEditor';
import { getValidatedGcsUrl } from '../../../../utils/url-validation';
import { tourAnchor } from '../../../tours/tourAnchors';
import { useTour } from '../../../tours/useTour';
import './VideosSection.css';

export const VideosSection = () => {
  // Get videos from context (fetched in DashboardScreen, re-fetched when filters change)
  const { videos, totalCount, loading, error, removeVideo, filters, setFilters, clearFilters, refresh } =
    useVideosContext();
  const { options, loading: optionsLoading, error: optionsError } = useVideoFilterOptions();
  const isFiltered = hasActiveVideoFilters(filters);
  const [selectedVideo, setSelectedVideo] = useState<VideoWithProspect | null>(null);
  // Tag edits made in the viewer, keyed by video id. The context's list is fetched once by
  // DashboardScreen, so this is what keeps the grid's pills honest without a refetch.
  const [tagEdits, setTagEdits] = useState<Record<string, VideoTag[]>>({});
  // The video being cut into clips. The viewer closes first so it is not playing behind the editor.
  const [clippingVideo, setClippingVideo] = useState<VideoWithProspect | null>(null);

  // Ready once the first page and the filter options are in: the tags step's anchor only renders
  // when there are tags to show. A later refetch keeps the grid on screen, so it stays ready.
  useTour('video', {
    ready: (!loading || videos.length > 0) && !optionsLoading,
    hasData: videos.length > 0,
  });

  const withEdits = (video: VideoWithProspect): VideoWithProspect =>
    tagEdits[video.id] ? { ...video, tags: tagEdits[video.id] } : video;

  const handleVideoClick = (videoId: string) => {
    const video = videos.find(v => v.id === videoId);
    if (video) {
      setSelectedVideo(withEdits(video));
    }
  };

  const handleClosePlayer = () => {
    setSelectedVideo(null);
  };

  const handleVideoDeleted = (videoId: string) => {
    removeVideo(videoId);
    setSelectedVideo(null);
    // Drop the tag overlay too, or a re-uploaded video reusing the id would inherit it.
    setTagEdits(current => {
      if (!current[videoId]) return current;
      const { [videoId]: _removed, ...rest } = current;
      return rest;
    });
  };

  const handleClip = (video: VideoWithProspect) => {
    setSelectedVideo(null);
    setClippingVideo(video);
  };

  // Stable per video: the clip editor reloads its media whenever this function changes.
  const getClippingVideoUrl = useCallback(
    async () => (clippingVideo ? getValidatedGcsUrl(clippingVideo.gcsUrl) : null),
    [clippingVideo]
  );

  const handleClipsCreated = async () => {
    try {
      await refresh();
    } catch (err) {
      console.error('[VideosSection] Failed to refresh after clipping', err);
    }
  };

  // Deleting a tag hides it on every video, so drop it from the local overlay and reload the grid.
  const handleTagDeleted = async (tagId: string) => {
    setTagEdits(current =>
      Object.fromEntries(
        Object.entries(current).map(([id, tags]) => [id, tags.filter(t => t.id !== tagId)])
      )
    );
    try {
      await refresh();
    } catch (err) {
      console.error('[VideosSection] Failed to refresh after deleting a tag', err);
    }
  };

  const handleTagsChange = (videoId: string, tags: VideoTag[]) => {
    setTagEdits(current => ({ ...current, [videoId]: tags }));
    setSelectedVideo(current =>
      current && current.id === videoId ? { ...current, tags } : current
    );
  };

  return (
    <ErrorBoundary
      fallbackTitle="Videos Section Error"
      fallbackMessage="We encountered an error loading the videos section. This may be temporary."
      onReset={() => {
        console.log('Videos section error boundary reset');
      }}
    >
      <div className="videos-section">
        {error && <div className="videos-section__error">{error}</div>}

        {optionsError && (
          <div className="videos-section__error">
            Some filter options could not be loaded. Search still works.
          </div>
        )}

        <VideoFilterBar
          filters={filters}
          onChange={setFilters}
          onClear={clearFilters}
          withTourAnchors
          options={options}
          optionsLoading={optionsLoading}
          resultCount={totalCount}
          shownCount={videos.length}
        />

        {/* Only on a cold load. Once there are results on screen they stay put while the
            next query runs — otherwise every keystroke tears the whole grid down and
            rebuilds it, re-requesting every thumbnail. */}
        {loading && videos.length === 0 && (
          <div className="videos-section__loading">
            <div className="videos-section__spinner"></div>
            <p>Loading videos...</p>
          </div>
        )}

        {!loading && videos.length === 0 && (
          // The grid's stand-in while there are no videos, so the page tour's grid step still points
          // at the spot the videos will fill rather than falling back to a centered card.
          <div className="videos-section__empty" {...tourAnchor('video.grid')}>
            {isFiltered ? (
              <p>
                No videos match these filters.{' '}
                <button
                  type="button"
                  className="videos-section__empty-clear"
                  onClick={clearFilters}
                >
                  Clear filters
                </button>
              </p>
            ) : (
              <p>No videos found</p>
            )}
          </div>
        )}

        {videos.length > 0 && (
          <VideosGrid
            videos={videos.map(withEdits)}
            loading={false}
            onVideoClick={handleVideoClick}
          />
        )}

        {selectedVideo && (
          <VideoViewerPanel
            video={selectedVideo}
            isOpen={true}
            onClose={handleClosePlayer}
            onTagsChange={handleTagsChange}
            onDeleted={handleVideoDeleted}
            onClip={handleClip}
            onTagDeleted={handleTagDeleted}
          />
        )}

        {clippingVideo && (
          <VideoClipEditor
            prospectId={clippingVideo.prospectId}
            videoId={clippingVideo.id}
            fallbackDurationSeconds={clippingVideo.durationSeconds}
            // The list already carries a signed url for this video; validated like the viewer's.
            getVideoUrl={getClippingVideoUrl}
            onClose={() => setClippingVideo(null)}
            onClipsCreated={handleClipsCreated}
          />
        )}
      </div>
    </ErrorBoundary>
  );
};
